import { IRequest } from 'legacy/lut/classes/models/onboarding/IRequest'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'

import { Crud } from 'core/types/Crud'

import { onboardingRequestApi, TFetchPartnerDataParams } from '../api/OnboardingRequestApi'
import {
  onboardingRequestDetailApi,
  TChangePartnerAPLParams,
} from '../api/OnboardingRequestDetailApi'

import { mapCaseOwnerSetDateInfo } from './mapping/mapCaseOwnerSetDateInfo'

class RequestService {
  public async fetchOneById(id: number) {
    const response = await this.fetchRequest(id)

    return response?.data.request ?? null
  }

  public fetchRequest(requestId: string | number) {
    const numericRequestId = Number(requestId)
    return numericRequestId > 0 ? onboardingRequestDetailApi.getRequest(numericRequestId) : null
  }

  public fetchChildrenRequests = (requestId: number) => {
    return requestId > 0 ? onboardingRequestDetailApi.getChildrenRequests(requestId) : null
  }

  public getListCrud(cacheKey: string) {
    return onboardingRequestApi.getRequestListCrud(cacheKey)
  }

  public getContractRequestListCrud(crudKey?: string): Crud<IRequest, number | string> {
    return onboardingRequestApi.getContractRequestListCrud(crudKey)
  }

  public searchAvailableRequestByEmail = (
    requestType: RequestTypes,
    email: string
  ): Promise<number | null> => {
    return onboardingRequestDetailApi.searchAvailableRequestByEmail(requestType, email)
  }

  public async reopenOne(requestId: number): Promise<true> {
    return onboardingRequestApi.reopenOne(requestId)
  }

  public async reviveOne(requestId: number): Promise<true> {
    return onboardingRequestApi.reviveOne(requestId)
  }

  public async removeOne(requestId: number) {
    return onboardingRequestApi.removeOne(requestId)
  }

  public async createAndStartOne(payload: IObjectAny) {
    const requestsSource = onboardingRequestApi.getRequestListCrud(payload.Type)
    const unOrderedRequestProps = payload.RequestProps
    const orderedRequestProps = Object.keys(unOrderedRequestProps)
      .sort()
      .reduce((r, k) => {
        r[k] = unOrderedRequestProps[k]
        return r
      }, {})

    const requestId = await onboardingRequestApi.createAndStartOne({
      ...payload,
      RequestProps: orderedRequestProps,
    })
    requestsSource.removeQueries()

    return requestId
  }

  public async createAndStartOneWithUid(payload: IObjectAny) {
    const requestsSource = onboardingRequestApi.getRequestListCrud(payload.Type)
    const unOrderedRequestProps = payload.RequestProps
    const orderedRequestProps = Object.keys(unOrderedRequestProps)
      .sort()
      .reduce((r, k) => {
        r[k] = unOrderedRequestProps[k]
        return r
      }, {})

    const request = await onboardingRequestApi.createAndStartOneWithUid({
      ...payload,
      RequestProps: orderedRequestProps,
    })
    requestsSource.removeQueries()

    return request
  }

  public async fetchOnePendingByPartnerAndType(partnerId: string, requestType: RequestTypes) {
    return onboardingRequestApi.fetchOnePendingByPartnerAndType(partnerId, requestType)
  }

  /**
   * @throws {ValidationParamsError | InvalidChangePartnerError | PartnerNotFoundError | Error}
   * @param requestId
   * @param partnerId
   */
  public changePartner(requestId: number, partnerId: string): Promise<boolean> {
    return onboardingRequestApi.changePartner(requestId, partnerId)
  }

  /**
   * @throws {ValidationParamsError | InvalidChangePartnerError | Error}
   * @param requestId
   */
  public canChangePartner(requestId: number): Promise<true> {
    return onboardingRequestApi.canChangePartner(requestId)
  }

  /**
   * @throws {ValidationParamsError | InvalidChangePartnerError | PartnerNotFoundError | Error}
   * @param requestId
   * @param partnerParams
   */
  public async canChangePartnerTarget(
    requestId: number,
    partnerParams: TFetchPartnerDataParams
  ): Promise<true> {
    return onboardingRequestApi.canChangePartnerTarget(requestId, partnerParams)
  }

  public getLastNChangesOfPartnerInRequest = (requestId: number, limit: number) => {
    return onboardingRequestApi.getLastNChangesOfPartnerInRequest(requestId, limit)
  }

  public setRequestFieldIsEligible = (requestId: number, value: boolean) => {
    return onboardingRequestDetailApi.setRequestFieldIsEligible(requestId, value)
  }

  public checkRequestFieldIsEligible = async (requestId: number) => {
    return onboardingRequestDetailApi.checkRequestFieldIsEligible(requestId)
  }

  public incrementSearchAttemptsCount = async (requestId: number) => {
    return onboardingRequestDetailApi.incrementSearchAttemptsCount(requestId)
  }

  public fetchSearchAttemptsCount = async (requestId: number) => {
    return onboardingRequestDetailApi.fetchSearchAttemptsCount(requestId)
  }

  public checkPartnerCanBeEligible = async (requestId: number) => {
    return onboardingRequestDetailApi.checkPartnerCanBeEligible(requestId)
  }

  public changePartnerAPL = async (params: TChangePartnerAPLParams) => {
    return onboardingRequestDetailApi.changePartnerAPL(params)
  }

  public getCaseOwnerSetDateInfo = async (requestId: number) => {
    const result = await onboardingRequestDetailApi.getCaseOwnerSetDateInfo(requestId)

    return result ? mapCaseOwnerSetDateInfo(result) : null
  }

  public updateCaseOwnerInRequestListForm = async (requestId: number, caseOwner: string) => {
    return onboardingRequestApi.updateCaseOwnerInRequestListForm(requestId, caseOwner)
  }

  public saveCaseOwnerSetDateInCollectedDataForm = async (requestId: number) => {
    return onboardingRequestDetailApi.saveCaseOwnerSetDateInCollectedDataForm(requestId)
  }
}

export const requestService = new RequestService()
